import { useCallback, useEffect } from 'react'
import type { ShapeTextParagraphsChangeOptions } from '@/components/app/shapes/shape-text-editor/hooks/use-shape-text-editing'
import type { Paragraph } from '@/api/types/stowed-element'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { NewShapeTextSeedStyle } from '@/lib/shapes/types/new-shape-text-seed-style'
import {
  defaultShapeTextParagraphs,
  isShapeTextFrame,
  isShapeTextSupported,
} from '@/lib/shapes/shape-text-frame'
import { applyStyleUpdateToParagraphs } from '@/lib/text/text-style-engine'
import type { TextRunStyleUpdate } from '@/lib/text/types/text-run-style-update'
import { getShapeById } from '../helpers/shape-list'
import type { ShapeManagerStateApi } from './use-shape-manager-state'
import type { ShapeUpdatesApi } from './use-shape-updates'
import type { ShapeTextCommitApi } from './use-shape-text-commit'
import type { ChainTextClipboardApi } from './use-chain-text-clipboard'

/**
 * A text frame's paragraphs for a whole-frame (no caret) read or style apply. A frame with
 * `paragraphs: []` (a converted or reloaded empty frame) has nothing for a style update to land on,
 * so it reads as the one blank paragraph typing would create. Without this, paragraph-level
 * settings such as Drop Cap were silently dropped on an empty frame.
 */
function wholeFrameParagraphs(shape: ShapeData, seed: NewShapeTextSeedStyle | undefined) {
  return shape.paragraphs?.length ? shape.paragraphs : defaultShapeTextParagraphs(seed)
}

export function useShapeTextEditing(
  ctx: ShapeManagerStateApi & ShapeUpdatesApi & ShapeTextCommitApi & ChainTextClipboardApi
) {
  const {
    cancelShapeTextCommit,
    chainTextSelectionRef,
    cloneShapeTextParagraphs,
    commitShapeTextNow,
    defaultShapeTextSeedStyle,
    editStartShapeTextParagraphsRef,
    editingShapeTextIdRef,
    mutateChainTextSelection,
    onShapeCommitted,
    onShapeTextEditEnter,
    onShapeTextEditExit,
    onShapeTextLiveEdit,
    scheduleShapeTextCommit,
    selectedShapeIdsRef,
    setChainTextSelection,
    setEditingShapeTextId,
    setSelectedShapeIds,
    setShapeTextCaretClientPoint,
    setShapeTextCaretOffset,
    shapeTextChainProjectedRef,
    shapeTextEditorRef,
    shapeTextProjectionInFlightRef,
    shapesRef,
    updateShape,
  } = ctx

  const applyShapeTextRunStyle = useCallback(
    (update: TextRunStyleUpdate, excludeShapeIds?: ReadonlySet<string>) => {
      // A cross-frame selection styles each member's own slice (WMPCM-9199). Checked before the
      // editing-frame branch, which would otherwise style only the frame the caret happens to be
      // in and silently ignore the rest of the selection.
      if (
        mutateChainTextSelection(
          (handle, part) => handle.applyRunStyleToRange(part.start, part.end, update),
          'reanchor'
        )
      ) {
        return
      }

      const editingId = editingShapeTextIdRef.current

      if (editingId) {
        // Every caller of applyShapeTextRunStyle is a discrete, deliberate style application
        // (a panel control, a canvas shortcut) — never a run of keystrokes that should coalesce
        // into one undo step (typing goes through handleTextChange, not this path) — so this
        // always commits immediately rather than through the 500ms keystroke debounce. Without
        // this, a fast Ctrl+Z (or the Undo button) right after e.g. the Type panel's Font Size
        // field found nothing pushed to the undo stack yet.
        shapeTextEditorRef.current?.applyRunStyle(update, { commitImmediately: true })
        return
      }

      const targetIds = selectedShapeIdsRef.current.filter((id) => {
        if (excludeShapeIds?.has(id)) return false

        const shape = getShapeById(shapesRef.current, id)

        return shape && isShapeTextSupported(shape)
      })

      for (const shapeId of targetIds) {
        const shape = getShapeById(shapesRef.current, shapeId)

        if (!shape) continue
        const beforeParagraphs = cloneShapeTextParagraphs(
          wholeFrameParagraphs(shape, defaultShapeTextSeedStyle)
        )
        const afterParagraphs = applyStyleUpdateToParagraphs(beforeParagraphs, update)

        updateShape(shapeId, { paragraphs: afterParagraphs })
        const latest = getShapeById(shapesRef.current, shapeId)

        if (latest) {
          onShapeCommitted?.(latest, 'update', {
            before: { paragraphs: beforeParagraphs },
            after: { paragraphs: afterParagraphs },
          })
        }
      }
    },
    [
      cloneShapeTextParagraphs,
      defaultShapeTextSeedStyle,
      mutateChainTextSelection,
      onShapeCommitted,
      updateShape,
      editingShapeTextIdRef,
      selectedShapeIdsRef,
      shapeTextEditorRef,
      shapesRef,
    ]
  )

  const getSelectedShapeTextParagraphs = useCallback((): Paragraph[] | null => {
    const editingId = editingShapeTextIdRef.current

    if (editingId) {
      const shape = getShapeById(shapesRef.current, editingId)

      if (!shape || !isShapeTextSupported(shape)) return null
      return shape.paragraphs ?? defaultShapeTextParagraphs()
    }

    // Whole-box Select-Tool selection (no active caret): aggregate every text-capable shape's own
    // paragraphs into one array, not just the first selected shape. The panel's Mixed-detection
    // logic (`isRunPropertyMixed`, `summarizeDropCapSelection`, etc.) already operates purely over
    // a flat `Paragraph[]` with no notion of "which shape this came from" — it was built to compare
    // multiple paragraphs within one shape, but that generalizes for free to multiple paragraphs
    // across multiple shapes once they're actually gathered here. Before this, selecting two text
    // boxes with different settings silently showed only the first shape's values with no Mixed
    // indication at all, and a panel edit would silently apply to both (`applyShapeTextRunStyle`
    // below already loops every selected shape — only the read side was single-shape).
    const paragraphs = selectedShapeIdsRef.current.flatMap((id) => {
      const shape = getShapeById(shapesRef.current, id)

      if (!shape || !isShapeTextSupported(shape)) return []
      return wholeFrameParagraphs(shape, defaultShapeTextSeedStyle)
    })

    return paragraphs.length > 0 ? paragraphs : null
  }, [defaultShapeTextSeedStyle, editingShapeTextIdRef, selectedShapeIdsRef, shapesRef])

  const getSelectedShapeIsTextFrame = useCallback((): boolean => {
    const editingId = editingShapeTextIdRef.current

    if (editingId) {
      const shape = getShapeById(shapesRef.current, editingId)

      return shape != null && isShapeTextFrame(shape)
    }

    // True if ANY selected shape is a genuine text frame — narrower controls gated on this (Drop
    // Cap) should stay interactive for the paragraphs that qualify rather than going fully disabled
    // just because a plain shape or a photo is also part of the same selection.
    return selectedShapeIdsRef.current.some((id) => {
      const shape = getShapeById(shapesRef.current, id)

      return shape != null && isShapeTextFrame(shape)
    })
  }, [editingShapeTextIdRef, selectedShapeIdsRef, shapesRef])

  const exitShapeTextEdit = useCallback(
    (opts?: { notifyExit?: boolean; activateSelectTool?: boolean }) => {
      // The baseline-diff commit below covers any pending debounced commit.
      cancelShapeTextCommit()

      const id = editingShapeTextIdRef.current

      if (!id) return

      const shape = getShapeById(shapesRef.current, id)

      if (!shape) {
        editingShapeTextIdRef.current = null
        setEditingShapeTextId(null)
        editStartShapeTextParagraphsRef.current = null
        return
      }

      const baseline = editStartShapeTextParagraphsRef.current
      const current = shape.paragraphs ?? defaultShapeTextParagraphs()

      // Clear the REF, not just the state, and do it before the commit below (WMPCM-9199).
      // `getEditingShapeTextId()` reads this ref, and `reflowAndPersistChain` uses it to decide
      // which frame the reflow must leave alone. `setEditingShapeTextId(null)` syncs the ref only
      // through a `useEffect`, so the reflow that this very commit triggers still saw the frame as
      // "editing" and skipped trimming it — the frame being exited kept text that by then also
      // lived downstream, and the next reflow evicted that same text a second time.
      editingShapeTextIdRef.current = null

      // Same projection clause as `commitShapeTextNow`: exiting must not be the one path that
      // silently drops the owner's chain redistribution.
      if (
        baseline != null &&
        (shapeTextChainProjectedRef.current || JSON.stringify(baseline) !== JSON.stringify(current))
      ) {
        onShapeCommitted?.(shape, 'update', {
          before: { paragraphs: cloneShapeTextParagraphs(baseline) },
          after: { paragraphs: cloneShapeTextParagraphs(current) },
        })
      }

      shapeTextChainProjectedRef.current = false
      editStartShapeTextParagraphsRef.current = null
      setEditingShapeTextId(null)
      setShapeTextCaretClientPoint(null)
      // A cross-frame selection belongs to the edit session that made it; leaving it painted
      // after the session ends would show a highlight nothing can act on (WMPCM-9199).
      chainTextSelectionRef.current = null
      setChainTextSelection(null)

      if (opts?.notifyExit !== false) {
        onShapeTextEditExit?.({ activateSelectTool: opts?.activateSelectTool })
      }
    },
    [
      cancelShapeTextCommit,
      cloneShapeTextParagraphs,
      onShapeCommitted,
      onShapeTextEditExit,
      chainTextSelectionRef,
      editStartShapeTextParagraphsRef,
      editingShapeTextIdRef,
      setChainTextSelection,
      setEditingShapeTextId,
      setShapeTextCaretClientPoint,
      shapeTextChainProjectedRef,
      shapesRef,
    ]
  )

  const enterShapeTextEdit = useCallback(
    (
      shapeId: string,
      opts?: {
        caretClientPoint?: { x: number; y: number }
        caretOffset?: number
        seedStyle?: NewShapeTextSeedStyle
      }
    ) => {
      const shape = getShapeById(shapesRef.current, shapeId)

      if (!shape || shape.hidden || shape.locked || !isShapeTextSupported(shape)) return

      if (editingShapeTextIdRef.current && editingShapeTextIdRef.current !== shapeId) {
        exitShapeTextEdit({ notifyExit: false })
      }

      setShapeTextCaretClientPoint(opts?.caretClientPoint ?? null)
      setShapeTextCaretOffset(opts?.caretOffset ?? null)

      // A caller-supplied seed wins; otherwise the book default (`defaultShapeTextSeedStyle`).
      const seedStyle = opts?.seedStyle ?? defaultShapeTextSeedStyle

      // "Never seeded" means no RUN anywhere, not just `paragraphs === undefined`: an empty array
      // or paragraphs whose lines carry no runs are just as style-less. Such a frame is laid out
      // and its caret sized from the 14pt engine default until the first keystroke, so a fresh 8pt
      // frame drew a 14pt caret and read Leading 16.8 (WMPCM-11367).
      const hasAnyRun =
        shape.paragraphs?.some((p) => p.textLines?.some((l) => (l.textRuns?.length ?? 0) > 0)) ===
        true
      // A run-less frame is re-seeded with a blank styled paragraph, but paragraph-level Drop Cap
      // settings already stored on its first paragraph belong to the frame and must survive that.
      const existingFirst = shape.paragraphs?.[0]
      const seededBlank = defaultShapeTextParagraphs(seedStyle)
      const seedParagraphs = hasAnyRun
        ? (shape.paragraphs ?? seededBlank)
        : existingFirst?.dropCap != null
          ? [
              {
                ...seededBlank[0],
                dropCap: existingFirst.dropCap,
                dropCapLines: existingFirst.dropCapLines,
                dropCapOffset: existingFirst.dropCapOffset,
              },
            ]
          : seededBlank

      if (!hasAnyRun) {
        updateShape(shapeId, { paragraphs: seedParagraphs })
      }

      const latest = getShapeById(shapesRef.current, shapeId)

      if (!latest) return

      editStartShapeTextParagraphsRef.current = cloneShapeTextParagraphs(
        latest.paragraphs ?? seedParagraphs
      )
      // Paired with the baseline above: the flag says "a projection has moved text since that
      // baseline was taken", so it has to be reset wherever the baseline is.
      shapeTextChainProjectedRef.current = false
      selectedShapeIdsRef.current = [shapeId]
      setSelectedShapeIds([shapeId])
      // Mirror of the synchronous clear in `exitShapeTextEdit`: the ref is the authoritative
      // "which frame is being edited" signal (`getEditingShapeTextId`), so it must lead the state
      // rather than trail it by a `useEffect`. Matters on the chain hand-off, where exit and
      // re-enter happen in one tick.
      editingShapeTextIdRef.current = shapeId
      setEditingShapeTextId(shapeId)
      onShapeTextEditEnter?.()
    },
    [
      cloneShapeTextParagraphs,
      defaultShapeTextSeedStyle,
      exitShapeTextEdit,
      onShapeTextEditEnter,
      updateShape,
      editStartShapeTextParagraphsRef,
      editingShapeTextIdRef,
      selectedShapeIdsRef,
      setEditingShapeTextId,
      setSelectedShapeIds,
      setShapeTextCaretClientPoint,
      setShapeTextCaretOffset,
      shapeTextChainProjectedRef,
      shapesRef,
    ]
  )

  /**
   * The single funnel for every editor-originated text write — typing, paste, IME, and the Type
   * panel's style applies (`use-shape-text-style.ts`) — which is why the live chain projection hangs
   * here rather than off the keystroke handler.
   *
   * Order matters: the projection re-fits the chain from the model, so this shape's own write has to
   * land first. It is fired before `scheduleShapeTextCommit` only for readability; the two do not
   * interact.
   */
  const handleShapeTextParagraphsChange = useCallback(
    (shapeId: string, paragraphs: Paragraph[], options?: ShapeTextParagraphsChangeOptions) => {
      updateShape(shapeId, { paragraphs })

      // Never mid-composition: the projection's trim would make the editor reassign the textarea's
      // value, which tears down the IME session the user is still typing into. The debounced commit
      // picks the reflow up when composition ends.
      //
      // Re-entrancy guard: the projection writes this very shape back (trimmed to capacity) through
      // `updateShape`. That path does not return here today, but it is one indirection away from
      // doing so, and a chain re-fit per keystroke is not something to re-enter.
      if (options?.isComposing !== true && !shapeTextProjectionInFlightRef.current) {
        shapeTextProjectionInFlightRef.current = true
        try {
          if (onShapeTextLiveEdit?.(shapeId) === true) shapeTextChainProjectedRef.current = true
        } finally {
          shapeTextProjectionInFlightRef.current = false
        }
      }

      if (options?.commitImmediately) {
        cancelShapeTextCommit()
        commitShapeTextNow(shapeId)
      } else {
        scheduleShapeTextCommit(shapeId)
      }
    },
    [
      cancelShapeTextCommit,
      commitShapeTextNow,
      onShapeTextLiveEdit,
      scheduleShapeTextCommit,
      updateShape,
      shapeTextChainProjectedRef,
      shapeTextProjectionInFlightRef,
    ]
  )

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      if (!editingShapeTextIdRef.current) return
      event.preventDefault()
      exitShapeTextEdit({ activateSelectTool: true })
    }

    window.addEventListener('keydown', onKeyDown)

    return () => window.removeEventListener('keydown', onKeyDown)
  }, [exitShapeTextEdit, editingShapeTextIdRef])

  return {
    applyShapeTextRunStyle,
    getSelectedShapeTextParagraphs,
    getSelectedShapeIsTextFrame,
    exitShapeTextEdit,
    enterShapeTextEdit,
    handleShapeTextParagraphsChange,
  }
}

export type ShapeTextEditingApi = ReturnType<typeof useShapeTextEditing>
